import {
  ADD_ERROR_MESSAGE,
  CLEAR_ERROR_MESSAGE,
  TOGGLE_CHAT_VISIBILITY,
  ADD_MESSAGE,
  ADD_BULK_MESSAGE,
  USER_TYPING,
  ASSISTANT_RESPONDING,
  DELETE_ALL_MESSAGES,
  DELETE_MESSAGES_KEEPING_INITIAL,
  SET_HAS_INITIALIZED_MESSAGES,
  ADD_SELECTED_VALUE,
  REMOVE_SELECTED_VALUE,
  REMOVE_ALL_SELECTED_VALUE,
  ADD_REPORT,
  LOAD_REPORTS,
  SET_ACTIVE_REPORT,
  APPEND_TO_LAST_MESSAGE,
  SET_CHAT_EXPANDED,
  SET_TASK_STATUS_MESSAGE,
  SHOW_CITATIONS,
  HIDE_CITATIONS,
  CLEAR_CITATIONS,
  SET_HISTORY_METADATA,
  CLEAR_HISTORY_METADATA,
  UPDATE_REPORT_MESSAGE,
  ENTER_SECTION_EDIT_MODE,
  EXIT_SECTION_EDIT_MODE,
  REVISE_SECTION_DRAFT_START,
  REVISE_SECTION_DRAFT_SUCCESS,
  REVISE_SECTION_DRAFT_FAILURE,
  SAVE_SECTION_EDIT_START,
  SAVE_SECTION_EDIT_SUCCESS,
  SAVE_SECTION_EDIT_FAILURE,
  DELETE_REPORT_SECTION,
  REORDER_REPORT_SECTIONS,
  MOVE_SECTION_UP,
  MOVE_SECTION_DOWN,
  ADD_NEW_SECTION_INPUT,
  REMOVE_NEW_SECTION_INPUT,
  ADD_NEW_SECTION_START,
  ADD_NEW_SECTION_SUCCESS,
  ADD_NEW_SECTION_FAILURE,
  SAVE_NEW_SECTION_START,
  SAVE_NEW_SECTION_SUCCESS,
  SAVE_NEW_SECTION_FAILURE,
  UPDATE_MESSAGE_FEEDBACK,
  SET_LAST_MESSAGE_TRACE_ID,
  SET_LAST_MESSAGE_SPEECH_STATUS,
  SET_FOLLOWUP_SUGGESTIONS,
  CLEAR_FOLLOWUP_SUGGESTIONS,
  SET_WELCOME_SUGGESTIONS,
  CLEAR_WELCOME_SUGGESTIONS,
  ATTACH_QUERY_PARAMS,
} from './actionTypes'
import { AI_ASSISTANT_CONSTANTS } from '../constants/aiAssistantConstants'
import { getLogoUrlFromCitation } from '../utils/logoDev'

const MESSAGE_FIELDS = [
  "text",
  "assumption",
  "link",
  "title",
  "description",
  "sources",
  "source_type",
  "grounding_sources",
  "chartConfig",
  "tableConfig",
  "report_id",
  "report_data",
  "report_status",
  "report_status_message",
  "report_title",
  "trace_id",
  "speechStatus",
];

const normalizeMessage = (message = {}) => {
  const payload = { ...(message.payload || {}) };

  MESSAGE_FIELDS.forEach((field) => {
    if (message[field] !== undefined && payload[field] === undefined) {
      payload[field] = message[field];
    }
  });

  const normalized = {
    ...message,
    type: message.type || "Harris",
    message_type: message.message_type || "text",
    payload,
  };

  MESSAGE_FIELDS.forEach((field) => {
    if (normalized[field] === undefined && payload[field] !== undefined) {
      normalized[field] = payload[field];
    }
  });

  return normalized;
};

const normalizeMessages = (messages = []) => messages.map(normalizeMessage);

let persistTimer = null;
const schedulePersist = (messages) => {
  if (persistTimer) clearTimeout(persistTimer);
  persistTimer = setTimeout(() => {
    try {
      localStorage.setItem("messages", JSON.stringify(messages));
    } catch (e) {
      // localStorage may be full; silently fail
    }
  }, 2000);
};

const persistMessages = (messages = []) => {
  const normalized = normalizeMessages(messages);
  schedulePersist(normalized);
  return normalized;
};

const isHarrisText = (msg) =>
  (msg.type || "").toLowerCase() === "harris" &&
  (msg.message_type || "text") === "text";

const findLastHarrisTextIndex = (messages) => {
  for (let i = messages.length - 1; i >= 0; i--) {
    if (isHarrisText(messages[i])) return i;
  }
  return -1;
};

const initialState = {
  messages: [],
  lastHarrisTextIndex: -1,
  isTyping: false,
  isAssistantResponding: false,
  hasInitializedMessages: false,
  selectedValues: [],
  isChatOpen: false,
  isChatExpanded: false,
  reports: [],
  activeReportId: null,
  errorMessage: '',
  taskStatusMessage: null,
  citations: [],
  isActionControllerVisible: false,
  isCitationsExpanded: false,
  actionControllerContent: null,
  historyMetadata: JSON.parse(localStorage.getItem("historyMetadata")) || null,
  followupSuggestions: [],
  welcomeSuggestions: [],
};

const chatReducer = (state = initialState, action) => {
  switch (action.type) {

    case TOGGLE_CHAT_VISIBILITY:
      return { ...state, isChatOpen: action.payload };

    case ADD_MESSAGE: {
      const updatedMessages = persistMessages([...state.messages, action.payload]);
      const newMsg = action.payload;
      const newIndex = isHarrisText(newMsg)
        ? updatedMessages.length - 1
        : state.lastHarrisTextIndex;
      return { ...state, messages: updatedMessages, lastHarrisTextIndex: newIndex };
    }

    case ADD_BULK_MESSAGE: {
      const updatedMessages = persistMessages(action.payload);
      return { ...state, messages: updatedMessages, lastHarrisTextIndex: findLastHarrisTextIndex(updatedMessages) };
    }

    case USER_TYPING:
      return { ...state, isTyping: action.payload };

    case ASSISTANT_RESPONDING:
      return { ...state, isAssistantResponding: action.payload };

    case ADD_ERROR_MESSAGE:
      return { ...state, errorMessage: action.payload };

    case CLEAR_ERROR_MESSAGE:
      return { ...state, errorMessage: '' };

    case DELETE_ALL_MESSAGES:
      window.localStorage.removeItem("messages");
      return { ...state, messages: [], lastHarrisTextIndex: -1 };

    case SET_HAS_INITIALIZED_MESSAGES:
      return { ...state, hasInitializedMessages: action.payload };

    case ADD_SELECTED_VALUE:
      return { ...state, selectedValues: [...state.selectedValues, action.payload] };

    case REMOVE_SELECTED_VALUE:
      return {
        ...state,
        selectedValues: state.selectedValues.filter((value) => value !== action.payload),
      };
    case REMOVE_ALL_SELECTED_VALUE:
      return {
        ...state,
        selectedValues: []
      }
    case DELETE_MESSAGES_KEEPING_INITIAL: {
      const keptMessages = persistMessages(state.messages.slice(0, action.payload));
      return {
        ...state,
        messages: keptMessages,
        lastHarrisTextIndex: findLastHarrisTextIndex(keptMessages),
      };
    }
    case APPEND_TO_LAST_MESSAGE: {
      const index = state.lastHarrisTextIndex;
      if (index === -1 || index >= state.messages.length) return state;

      const lastMessage = state.messages[index];
      const nextText =
        (lastMessage.payload?.text || lastMessage.text || "") + action.payload;
      const updatedLastMessage = {
        ...lastMessage,
        text: nextText,
        payload: { ...(lastMessage.payload || {}), text: nextText },
      };
      const updatedMessages = [
        ...state.messages.slice(0, index),
        updatedLastMessage,
        ...state.messages.slice(index + 1),
      ];

      const normalizedMessages = persistMessages(updatedMessages);
      return { ...state, messages: normalizedMessages };
    }
    case SET_LAST_MESSAGE_SPEECH_STATUS: {
      const index = state.lastHarrisTextIndex;
      if (index === -1 || index >= state.messages.length) return state;

      const lastMessage = state.messages[index];
      const updatedLastMessage = {
        ...lastMessage,
        speechStatus: action.payload,
        payload: { ...(lastMessage.payload || {}), speechStatus: action.payload },
      };
      const updatedMessages = [
        ...state.messages.slice(0, index),
        updatedLastMessage,
        ...state.messages.slice(index + 1),
      ];

      const normalizedMessages = persistMessages(updatedMessages);
      return { ...state, messages: normalizedMessages };
    }
    case ADD_REPORT: {
      const existingReport = state.reports.find(r => r.id === action.payload.id);
      let newReports;
      if (existingReport) {
        newReports = state.reports.map(r => r.id === action.payload.id ? action.payload : r);
      } else {
        newReports = [...state.reports, action.payload];
      }
      localStorage.setItem("ai_reports", JSON.stringify(newReports));
      return { ...state, reports: newReports, activeReportId: action.payload.id };
    }

    case SET_ACTIVE_REPORT:
      return { ...state, activeReportId: action.payload };

    case LOAD_REPORTS:
      return { ...state, reports: action.payload };

    case SET_CHAT_EXPANDED:
      return {
        ...state,
        isChatExpanded: action.payload,
      };
    case SET_TASK_STATUS_MESSAGE:
      return {
        ...state,
        taskStatusMessage: action.payload,
      };
    case SHOW_CITATIONS: {
      const payload = Array.isArray(action.payload) ? action.payload : [];
      const logoToken = AI_ASSISTANT_CONSTANTS.LOGO_DEV_PUBLISHABLE_KEY || '';
      const transformedCitations = payload.map((source) => {
        const link = source.link ?? source.url ?? '';
        const logo =
          source.icon ??
          getLogoUrlFromCitation({ link }, logoToken, 32) ??
          null;
        return {
          title: source.title ?? source.tile ?? '',
          logo,
          date: source.date ?? '',
          link,
        };
      });
      return {
        ...state,
        citations: transformedCitations,
        isActionControllerVisible: true,
        isCitationsExpanded: true,
        actionControllerContent: 'Citations',
      };
    }
    case HIDE_CITATIONS:
      return {
        ...state,
        isActionControllerVisible: false,
        isCitationsExpanded: false,
        actionControllerContent: null,
      };
    case CLEAR_CITATIONS:
      return {
        ...state,
        citations: [],
        isActionControllerVisible: false,
        isCitationsExpanded: false,
        actionControllerContent: null,
      };
    case SET_HISTORY_METADATA:
      localStorage.setItem("historyMetadata", JSON.stringify(action.payload));
      return {
        ...state,
        historyMetadata: action.payload,
      };
    case CLEAR_HISTORY_METADATA:
      localStorage.removeItem("historyMetadata");
      return {
        ...state,
        historyMetadata: null,
      };
    case DELETE_REPORT_SECTION: {
      const { reportId, sectionId } = action.payload;
      const updatedMessages = state.messages.map(msg => {
        if (msg.message_type === 'report' && msg.report_id === reportId) {
          return {
            ...msg,
            report_data: {
              ...msg.report_data,
              sections: msg.report_data.sections.filter(
                section => section.section_id !== sectionId
              ),
            },
          };
        }
        return msg;
      });
      const normalizedMessages = persistMessages(updatedMessages);
      return { ...state, messages: normalizedMessages };
    }
    case SET_FOLLOWUP_SUGGESTIONS:
      return {
        ...state,
        followupSuggestions: Array.isArray(action.payload) ? action.payload : [],
      };
    case CLEAR_FOLLOWUP_SUGGESTIONS:
      return {
        ...state,
        followupSuggestions: [],
      };
    case SET_WELCOME_SUGGESTIONS:
      return {
        ...state,
        welcomeSuggestions: Array.isArray(action.payload) ? action.payload : [],
      };
    case CLEAR_WELCOME_SUGGESTIONS:
      return {
        ...state,
        welcomeSuggestions: [],
      };
    case REORDER_REPORT_SECTIONS: {
      const { reportId, newSections } = action.payload;
      const updatedMessages = state.messages.map(msg => {
        if (msg.message_type === 'report' && msg.report_id === reportId) {
          return {
            ...msg,
            report_data: {
              ...msg.report_data,
              sections: newSections,
            },
          };
        }
        return msg;
      });
      const normalizedMessages = persistMessages(updatedMessages);
      return { ...state, messages: normalizedMessages };
    }
    case MOVE_SECTION_UP: {
      const { reportId, sectionId } = action.payload;
      const updatedMessages = state.messages.map(msg => {
        if (msg.message_type === 'report' && msg.report_id === reportId) {
          const sections = [...msg.report_data.sections];
          const index = sections.findIndex(s => s.section_id === sectionId);
          if (index > 0) {
            const temp = sections[index];
            sections[index] = sections[index - 1];
            sections[index - 1] = temp;
          }
          return {
            ...msg,
            report_data: {
              ...msg.report_data,
              sections,
            },
          };
        }
        return msg;
      });
      const normalizedMessages = persistMessages(updatedMessages);
      return { ...state, messages: normalizedMessages };
    }
    case MOVE_SECTION_DOWN: {
      const { reportId, sectionId } = action.payload;
      const updatedMessages = state.messages.map(msg => {
        if (msg.message_type === 'report' && msg.report_id === reportId) {
          const sections = [...msg.report_data.sections];
          const index = sections.findIndex(s => s.section_id === sectionId);
          if (index < sections.length - 1) {
            const temp = sections[index];
            sections[index] = sections[index + 1];
            sections[index + 1] = temp;
          }
          return {
            ...msg,
            report_data: {
              ...msg.report_data,
              sections,
            },
          };
        }
        return msg;
      });
      const normalizedMessages = persistMessages(updatedMessages);
      return { ...state, messages: normalizedMessages };
    }
    case ADD_NEW_SECTION_INPUT: {
      const { reportId, afterSectionId } = action.payload;
      const updatedMessages = state.messages.map(msg => {
        if (msg.message_type === 'report' && msg.report_id === reportId) {
          const sections = msg.report_data.sections;
          const placeholderExists = sections.some(section =>
            section.type === 'new_section_input' &&
            (section.after_section_id ?? null) === (afterSectionId ?? null)
          );
          if (placeholderExists) {
            return msg;
          }
          const placeholder = {
            type: 'new_section_input',
            after_section_id: afterSectionId ?? null,
            draft_id: null,
            loading: false,
            error: null,
            conversation: [],
            candidate_section: null,
          };
          let hasInserted = false;
          const newSections = sections.reduce((acc, section) => {
            acc.push(section);
            if (!hasInserted && afterSectionId && section.section_id === afterSectionId) {
              acc.push(placeholder);
              hasInserted = true;
            }
            return acc;
          }, []);
          if (!hasInserted) {
            newSections.push(placeholder);
          }
          return {
            ...msg,
            report_data: {
              ...msg.report_data,
              sections: newSections,
            },
          };
        }
        return msg;
      });
      const normalizedMessages = persistMessages(updatedMessages);
      return { ...state, messages: normalizedMessages };
    }
    case REMOVE_NEW_SECTION_INPUT: {
      const { reportId, draftId, afterSectionId } = action.payload;
      const updatedMessages = state.messages.map(msg => {
        if (msg.message_type === 'report' && msg.report_id === reportId) {
          let removed = false;
          const newSections = msg.report_data.sections.filter(section => {
            if (removed || section.type !== 'new_section_input') {
              return true;
            }
            const matches =
              (draftId && section.draft_id === draftId) ||
              (draftId == null && (section.after_section_id ?? null) === (afterSectionId ?? null));
            if (matches) {
              removed = true;
              return false;
            }
            return true;
          });
          return {
            ...msg,
            report_data: {
              ...msg.report_data,
              sections: newSections,
            },
          };
        }
        return msg;
      });
      const normalizedMessages = persistMessages(updatedMessages);
      return { ...state, messages: normalizedMessages };
    }
    case ADD_NEW_SECTION_START: {
      const { reportId, afterSectionId, draftId } = action.payload;
      const updatedMessages = state.messages.map(msg => {
        if (msg.message_type === 'report' && msg.report_id === reportId) {
          const newSections = msg.report_data.sections.map(section => {
            if (section.type !== 'new_section_input') {
              return section;
            }
            const slotMatches = (section.after_section_id ?? null) === (afterSectionId ?? null);
            const hasDraftId = Boolean(section.draft_id);
            const matches =
              (draftId && section.draft_id === draftId) ||
              (!hasDraftId && slotMatches);
            if (matches) {
              return { ...section, loading: true, error: null };
            }
            return section;
          });
          return {
            ...msg,
            report_data: {
              ...msg.report_data,
              sections: newSections,
            },
          };
        }
        return msg;
      });
      const normalizedMessages = persistMessages(updatedMessages);
      return { ...state, messages: normalizedMessages };
    }
    case ADD_NEW_SECTION_SUCCESS: {
      const { reportId, draftId, previousDraftId, candidateSection, afterSectionId, turns } = action.payload;
      const updatedMessages = state.messages.map(msg => {
        if (msg.message_type === 'report' && msg.report_id === reportId) {
          const newSections = msg.report_data.sections.map(section => {
            if (section.type !== 'new_section_input') {
              return section;
            }
            const slotMatches = (section.after_section_id ?? null) === (afterSectionId ?? null);
            const hasDraftId = Boolean(section.draft_id);
            const matches =
              (draftId && section.draft_id === draftId) ||
              (previousDraftId && section.draft_id === previousDraftId) ||
              (!hasDraftId && slotMatches);
            if (matches) {
              return {
                ...section,
                draft_id: draftId ?? section.draft_id,
                loading: false,
                error: null,
                candidate_section: candidateSection,
                conversation: turns,
              };
            }
            return section;
          });
          return {
            ...msg,
            report_data: {
              ...msg.report_data,
              sections: newSections,
            },
          };
        }
        return msg;
      });
      const normalizedMessages = persistMessages(updatedMessages);
      return { ...state, messages: normalizedMessages };
    }

    case ADD_NEW_SECTION_FAILURE: {
      const { reportId, error, afterSectionId, draftId } = action.payload;
      const updatedMessages = state.messages.map(msg => {
        if (msg.message_type === 'report' && msg.report_id === reportId) {
          const newSections = msg.report_data.sections.map(section => {
            if (section.type !== 'new_section_input') {
              return section;
            }
            const slotMatches = (section.after_section_id ?? null) === (afterSectionId ?? null);
            const hasDraftId = Boolean(section.draft_id);
            const matches =
              (draftId && section.draft_id === draftId) ||
              (!hasDraftId && slotMatches);
            if (matches) {
              return { ...section, loading: false, error: error };
            }
            return section;
          });
          return {
            ...msg,
            report_data: {
              ...msg.report_data,
              sections: newSections,
            },
          };
        }
        return msg;
      });
      const normalizedMessages = persistMessages(updatedMessages);
      return { ...state, messages: normalizedMessages };
    }

    case ENTER_SECTION_EDIT_MODE: {
      const { reportId, sectionId } = action.payload;
      const updatedMessages = state.messages.map(msg => {
        if (msg.message_type === 'report' && msg.report_id === reportId) {
          const newSections = msg.report_data.sections.map(section => {
            if (section.section_id === sectionId) {
              if (section.edit_state?.active) {
                return section;
              }
              return {
                ...section,
                edit_state: {
                  active: true,
                  draft_id: section.edit_state?.draft_id ?? null,
                  loading: false,
                  saving: false,
                  error: null,
                  conversation: section.edit_state?.conversation ?? [],
                  candidate_section: section.edit_state?.candidate_section ?? null,
                },
              };
            }
            return section;
          });
          return {
            ...msg,
            report_data: {
              ...msg.report_data,
              sections: newSections,
            },
          };
        }
        return msg;
      });
      const normalizedMessages = persistMessages(updatedMessages);
      return { ...state, messages: normalizedMessages };
    }
    case EXIT_SECTION_EDIT_MODE: {
      const { reportId, sectionId } = action.payload;
      const updatedMessages = state.messages.map(msg => {
        if (msg.message_type === 'report' && msg.report_id === reportId) {
          const newSections = msg.report_data.sections.map(section => {
            if (section.section_id === sectionId && section.edit_state?.active) {
              const { edit_state, ...rest } = section;
              return {
                ...rest,
                edit_state: undefined,
                loading: false,
              };
            }
            return section;
          });
          return {
            ...msg,
            report_data: {
              ...msg.report_data,
              sections: newSections,
            },
          };
        }
        return msg;
      });
      const normalizedMessages = persistMessages(updatedMessages);
      return { ...state, messages: normalizedMessages };
    }
    case REVISE_SECTION_DRAFT_START: {
      const { reportId, sectionId } = action.payload;
      const updatedMessages = state.messages.map(msg => {
        if (msg.message_type === 'report' && msg.report_id === reportId) {
          const newSections = msg.report_data.sections.map(section => {
            if (section.section_id === sectionId && section.edit_state?.active) {
              return {
                ...section,
                edit_state: {
                  ...section.edit_state,
                  loading: true,
                  saving: false,
                  error: null,
                },
              };
            }
            return section;
          });
          return {
            ...msg,
            report_data: {
              ...msg.report_data,
              sections: newSections,
            },
          };
        }
        return msg;
      });
      const normalizedMessages = persistMessages(updatedMessages);
      return { ...state, messages: normalizedMessages };
    }
    case REVISE_SECTION_DRAFT_SUCCESS: {
      const { reportId, sectionId, draftId, candidateSection, turns } = action.payload;
      const updatedMessages = state.messages.map(msg => {
        if (msg.message_type === 'report' && msg.report_id === reportId) {
          const newSections = msg.report_data.sections.map(section => {
            if (section.section_id === sectionId && section.edit_state?.active) {
              return {
                ...section,
                edit_state: {
                  active: true,
                  draft_id: draftId,
                  loading: false,
                  saving: false,
                  error: null,
                  conversation: turns,
                  candidate_section: candidateSection,
                },
              };
            }
            return section;
          });
          return {
            ...msg,
            report_data: {
              ...msg.report_data,
              sections: newSections,
            },
          };
        }
        return msg;
      });
      const normalizedMessages = persistMessages(updatedMessages);
      return { ...state, messages: normalizedMessages };
    }
    case REVISE_SECTION_DRAFT_FAILURE: {
      const { reportId, sectionId, error } = action.payload;
      const updatedMessages = state.messages.map(msg => {
        if (msg.message_type === 'report' && msg.report_id === reportId) {
          const newSections = msg.report_data.sections.map(section => {
            if (section.section_id === sectionId && section.edit_state?.active) {
              return {
                ...section,
                edit_state: {
                  ...section.edit_state,
                  loading: false,
                  saving: false,
                  error: error,
                },
              };
            }
            return section;
          });
          return {
            ...msg,
            report_data: {
              ...msg.report_data,
              sections: newSections,
            },
          };
        }
        return msg;
      });
      const normalizedMessages = persistMessages(updatedMessages);
      return { ...state, messages: normalizedMessages };
    }
    case SAVE_SECTION_EDIT_START: {
      const { reportId, sectionId } = action.payload;
      const updatedMessages = state.messages.map(msg => {
        if (msg.message_type === 'report' && msg.report_id === reportId) {
          const newSections = msg.report_data.sections.map(section => {
            if (section.section_id === sectionId && section.edit_state?.active) {
              return {
                ...section,
                loading: true,
                edit_state: {
                  ...section.edit_state,
                  loading: true,
                  saving: true,
                  error: null,
                },
              };
            }
            return section;
          });
          return {
            ...msg,
            report_data: {
              ...msg.report_data,
              sections: newSections,
            },
          };
        }
        return msg;
      });
      const normalizedMessages = persistMessages(updatedMessages);
      return { ...state, messages: normalizedMessages };
    }
    case SAVE_SECTION_EDIT_SUCCESS: {
      const { reportId, sectionId, updatedSection } = action.payload;
      const updatedMessages = state.messages.map(msg => {
        if (msg.message_type === 'report' && msg.report_id === reportId) {
          const newSections = msg.report_data.sections.map(section => {
            if (section.section_id === sectionId) {
              return {
                ...section,
                ...updatedSection,
                loading: false,
                edit_state: undefined,
              };
            }
            return section;
          });
          const nextMsg = {
            ...msg,
            report_data: {
              ...msg.report_data,
              sections: newSections,
            },
          };
          return nextMsg;
        }
        return msg;
      });
      const normalizedMessages = persistMessages(updatedMessages);
      return { ...state, messages: normalizedMessages };
    }
    case SAVE_SECTION_EDIT_FAILURE: {
      const { reportId, sectionId, error } = action.payload;
      const updatedMessages = state.messages.map(msg => {
        if (msg.message_type === 'report' && msg.report_id === reportId) {
          const newSections = msg.report_data.sections.map(section => {
            if (section.section_id === sectionId && section.edit_state?.active) {
              return {
                ...section,
                loading: false,
                edit_state: {
                  ...section.edit_state,
                  loading: false,
                  saving: false,
                  error: error,
                },
              };
            }
            return section;
          });
          return {
            ...msg,
            report_data: {
              ...msg.report_data,
              sections: newSections,
            },
          };
        }
        return msg;
      });
      const normalizedMessages = persistMessages(updatedMessages);
      return { ...state, messages: normalizedMessages };
    }
    case SAVE_NEW_SECTION_START: {
      const { reportId, draftId, afterSectionId } = action.payload;
      const updatedMessages = state.messages.map(msg => {
        if (msg.message_type === 'report' && msg.report_id === reportId) {
          const newSections = msg.report_data.sections.map(section => {
            if (section.type !== 'new_section_input') {
              return section;
            }
            const matches =
              section.draft_id === draftId ||
              (section.draft_id == null && (section.after_section_id ?? null) === (afterSectionId ?? null));
            if (matches) {
              return { ...section, loading: true, error: null };
            }
            return section;
          });
          return {
            ...msg,
            report_data: {
              ...msg.report_data,
              sections: newSections,
            },
          };
        }
        return msg;
      });
      const normalizedMessages = persistMessages(updatedMessages);
      return { ...state, messages: normalizedMessages };
    }
    case SAVE_NEW_SECTION_SUCCESS: {
      const { reportId, draftId, afterSectionId, newSection } = action.payload;
      const updatedMessages = state.messages.map(msg => {
        if (msg.message_type === 'report' && msg.report_id === reportId) {
          const newSections = msg.report_data.sections.flatMap(section => {
            if (section.type === 'new_section_input') {
              const matches =
                section.draft_id === draftId ||
                (section.draft_id == null && (section.after_section_id ?? null) === (afterSectionId ?? null));
              if (matches) {
                return [newSection];
              }
            }
            return [section];
          });
          return {
            ...msg,
            report_data: {
              ...msg.report_data,
              sections: newSections,
            },
          };
        }
        return msg;
      });
      const normalizedMessages = persistMessages(updatedMessages);
      return { ...state, messages: normalizedMessages };
    }
    case SAVE_NEW_SECTION_FAILURE: {
      const { reportId, draftId, afterSectionId, error } = action.payload;
      const updatedMessages = state.messages.map(msg => {
        if (msg.message_type === 'report' && msg.report_id === reportId) {
          const newSections = msg.report_data.sections.map(section => {
            if (section.type !== 'new_section_input') {
              return section;
            }
            const matches =
              section.draft_id === draftId ||
              (section.draft_id == null && (section.after_section_id ?? null) === (afterSectionId ?? null));
            if (matches) {
              return { ...section, loading: false, error: error };
            }
            return section;
          });
          return {
            ...msg,
            report_data: {
              ...msg.report_data,
              sections: newSections,
            },
          };
        }
        return msg;
      });
      const normalizedMessages = persistMessages(updatedMessages);
      return { ...state, messages: normalizedMessages };
    }
    case UPDATE_MESSAGE_FEEDBACK: {
      const { messageIndex, rating } = action.payload;
      if (typeof messageIndex !== "number" || messageIndex < 0 || messageIndex >= state.messages.length) {
        return state;
      }
      const updatedMessages = state.messages.map((msg, i) =>
        i === messageIndex
          ? { ...msg, feedback: rating === null ? undefined : { rating } }
          : msg,
      );
      const normalizedMessages = persistMessages(updatedMessages);
      return { ...state, messages: normalizedMessages };
    }
    case SET_LAST_MESSAGE_TRACE_ID: {
      const traceId = action.payload;
      const index = state.lastHarrisTextIndex;
      if (typeof traceId !== "string" || !traceId || index < 0 || index >= state.messages.length) {
        return state;
      }
      const updatedMessages = state.messages.map((msg, i) =>
        i === index ? { ...msg, trace_id: traceId } : msg,
      );
      const normalizedMessages = persistMessages(updatedMessages);
      return { ...state, messages: normalizedMessages };
    }
    case UPDATE_REPORT_MESSAGE: {
      const { report_id, updatedData } = action.payload;
      const updatedMessages = state.messages.map(msg => {
        if (msg.report_id === report_id) {
          const nextReportId = updatedData.report_id ?? msg.report_id;
          const nextPayload = {
            ...(msg.payload || {}),
            report_id: nextReportId,
            report_status: updatedData.report_status ?? msg.report_status,
            report_status_message: updatedData.report_status_message ?? msg.report_status_message,
            report_title: updatedData.report_title ?? msg.report_title,
            report_data: updatedData.report_data ?? msg.report_data,
          };
          return {
            ...msg,
            report_id: nextReportId,
            report_status: updatedData.report_status ?? msg.report_status,
            report_status_message: updatedData.report_status_message ?? msg.report_status_message,
            report_title: updatedData.report_title ?? msg.report_title,
            report_data: updatedData.report_data ?? msg.report_data,
            payload: nextPayload,
          };
        }
        return msg;
      });
      const normalizedMessages = persistMessages(updatedMessages);
      return { ...state, messages: normalizedMessages };
    }

    case ATTACH_QUERY_PARAMS: {
      const index = state.lastHarrisTextIndex;
      if (index === -1 || index >= state.messages.length) return state;

      const lastMessage = state.messages[index];
      const updatedLastMessage = {
        ...lastMessage,
        payload: {
          ...(lastMessage.payload || {}),
          query_params_data: action.payload.data,
        },
        query_params_data: action.payload.data,
      };

      const updatedMessages = [
        ...state.messages.slice(0, index),
        updatedLastMessage,
        ...state.messages.slice(index + 1),
      ];

      const normalizedMessages = persistMessages(updatedMessages);
      return { ...state, messages: normalizedMessages };
    }

    default:
      return state;
  }
};

export default chatReducer;
